"use strict";
/**
 * Salesforce v2 sync criteria: the rules that choose which Leads and Contacts
 * come in. Pure functions with no imports, so the API (the save route, the
 * poller's loader) and the screen share one reading of the stored value, and the
 * browser never bundles the matcher.
 *
 * The stored value is `account.data.serviceConfig.salesforce.syncCriteria`:
 *
 *   { Lead:    { filterProperties?: SyncRule[], rules?: SyncRulesV1 },
 *     Contact: { filterProperties?: SyncRule[], rules?: SyncRulesV1 } }
 *
 * `filterProperties` is the list the old tab and v1 read (one AND group, no
 * Keep out, the 14 legacy words). `rules` is the new shape: groups joined by OR,
 * the rules of a group joined by AND, and one Keep out list. While `rules`
 * exists it wins and `filterProperties` is not read.
 *
 * The Opportunity side (Lane 1 of the Opportunity sync) is not a people side. It is
 * `{ off?: true, windowMonths?: 1..12, rules?: SyncRulesV1 }`: the switch, the time
 * window and the rules. It has no `filterProperties`, and `SyncObject` does not
 * name it (the people code that loops over SYNC_OBJECTS must never see it); the
 * functions below that are about it say Opportunity in their names.
 *
 * The Campaign side (CMP1 of the Campaign sync) is not a people side either. It is
 * `{ off?: false, windowMonths?: 1..12, includeActive?: boolean, includeRecent?:
 * boolean, picks?: string[], rules?: SyncRulesV1 }`. The one thing that sets it
 * apart from the Opportunity side: it is OFF until the customer chooses (only a
 * stored `off: false` turns it on). `SyncObject` does not name it either; what it
 * means is read by campaignCriteriaOfStored (helpers/salesforce/campaignSync.ts).
 * This file holds only what the save check needs of it, because campaignSync.ts
 * imports this file and not the other way round.
 *
 * Two checks, and they never swap places:
 *  - readableOrWhy: the loader's check. It refuses only what makes the answer
 *    unknowable, so that nothing which syncs today stops on release day. It
 *    never applies a size limit.
 *  - validateForSave: the save check. Everything, limits included, applied
 *    only to the sides a save changes.
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CAMPAIGN_SIDE_MAX_PICKS = exports.CAMPAIGN_SIDE_WINDOW = exports.CAMPAIGN_SIDE_KEYS = exports.validateForSave = exports.readableOrWhy = exports.toStoredOpportunity = exports.opportunityKeyInputOf = exports.opportunityChangeOf = exports.rulesAreOnlyTighter = exports.sameOpportunityMatching = exports.opportunityWindowOf = exports.opportunityOffOf = exports.readOpportunitySide = exports.storedOpportunitySideOf = exports.isOpportunityWindow = exports.OPPORTUNITY_SIDE_KEYS = exports.OPPORTUNITY_SIDE_WINDOW = exports.sameMatching = exports.sidesWithRules = exports.criteriaKeyInputOf = exports.toStored = exports.ruleToStored = exports.fieldsOf = exports.isFlat = exports.readSide = exports.readSideOf = exports.storedSideOf = exports.conditionsFor = exports.conditionLabel = exports.isFiniteNumberText = exports.isRealDate = exports.takesDate = exports.takesValueList = exports.takesCommaList = exports.takesNoValue = exports.FIELD_NAME = exports.LIMITS = exports.NEW_CONDITIONS = exports.LEGACY_CONDITIONS = exports.CAMPAIGN_SIDE = exports.OPPORTUNITY_SIDE = exports.SYNC_OBJECTS = void 0;
exports.SYNC_OBJECTS = ["Lead", "Contact"];
/**
 * The key of the Opportunity side of a stored `syncCriteria` value. It is not a
 * SyncObject: Opportunity has no `filterProperties` and no people to sync, and the
 * code that loops over SYNC_OBJECTS (the poller's loader, the count of a change
 * of people rules, the generic save routes) must not see it.
 */
exports.OPPORTUNITY_SIDE = "Opportunity";
/**
 * The key of the Campaign side of a stored `syncCriteria` value. Not a SyncObject,
 * for the same reasons as Opportunity: no `filterProperties`, no people to sync.
 */
exports.CAMPAIGN_SIDE = "Campaign";
/** The 14 words the old tab and v1 know, in the order the service's schema has them. */
exports.LEGACY_CONDITIONS = [
    "is",
    "is not",
    "contains",
    "does not contain",
    "starts with",
    "does not start with",
    "ends with",
    "does not end with",
    "is more than",
    "is less than",
    "is empty",
    "is not empty",
    "is one of",
    "is not one of",
];
/** The 5 words that exist only inside `rules`, spelled as Segments store them. */
exports.NEW_CONDITIONS = [
    "on",
    "before",
    "after",
    "is any of",
    "is not any of",
];
/** The limits a save applies to what it changes (never the loader). */
exports.LIMITS = {
    /** groups per object */
    groups: 10,
    /** rules in one group (a flat list is one group) */
    rulesPerGroup: 20,
    /** rules in Keep out */
    keepOut: 20,
    /** rules per object, all lists together */
    rulesPerObject: 50,
    /** characters of a field name */
    fieldName: 255,
    /** characters of a value */
    value: 1000,
    /** comma separated values of is one of / is not one of, and items of a list */
    listValues: 100,
    /** characters of one item of propertyValues */
    listItem: 255,
    /** bytes of the whole value as JSON */
    bytes: 64 * 1024,
};
/**
 * The same shape soql.ts accepts for a field in a SELECT list (and the poller's
 * FIELD_NAME in readableMapping.ts, which a spec compares this with).
 */
exports.FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)*$/;
const DATE_TEXT = /^(\d{4})-(\d{2})-(\d{2})$/;
// ---------------------------------------------------------------------------
// Small helpers
const isPlainObject = (value) => typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date);
const isLegacy = (condition) => exports.LEGACY_CONDITIONS.includes(condition);
const isNew = (condition) => exports.NEW_CONDITIONS.includes(condition);
/** Is empty / is not empty: the rule is about whether the field has a value. */
const takesNoValue = (condition) => condition === "is empty" || condition === "is not empty";
exports.takesNoValue = takesNoValue;
/** is one of / is not one of: one string, the values separated by commas. */
const takesCommaList = (condition) => condition === "is one of" || condition === "is not one of";
exports.takesCommaList = takesCommaList;
/** is any of / is not any of: a list in `propertyValues`. */
const takesValueList = (condition) => condition === "is any of" || condition === "is not any of";
exports.takesValueList = takesValueList;
/** is on / is before / is after: a day, YYYY-MM-DD. */
const takesDate = (condition) => condition === "on" || condition === "before" || condition === "after";
exports.takesDate = takesDate;
const takesNumber = (condition) => condition === "is more than" || condition === "is less than";
/** YYYY-MM-DD and a day the calendar has. */
const isRealDate = (value) => {
    if (typeof value !== "string")
        return false;
    const match = DATE_TEXT.exec(value);
    if (!match)
        return false;
    const [year, month, day] = match.slice(1, 4).map(Number);
    if (year < 1 || month < 1 || month > 12 || day < 1)
        return false;
    const utc = new Date(Date.UTC(year, month - 1, day));
    // Feb 30 rolls over into March: not a date
    return (utc.getUTCFullYear() === year &&
        utc.getUTCMonth() === month - 1 &&
        utc.getUTCDate() === day);
};
exports.isRealDate = isRealDate;
/** Non-blank text that Number() reads as a finite number. */
const isFiniteNumberText = (value) => typeof value === "string" &&
    value.trim() !== "" &&
    Number.isFinite(Number(value));
exports.isFiniteNumberText = isFiniteNumberText;
/** Deep equality for JSON values (key order does not matter, array order does). */
const deepEqual = (a, b) => {
    if (a === b)
        return true;
    if (Array.isArray(a)) {
        return (Array.isArray(b) &&
            a.length === b.length &&
            a.every((item, index) => deepEqual(item, b[index])));
    }
    if (isPlainObject(a)) {
        if (!isPlainObject(b))
            return false;
        const keys = Object.keys(a).filter((key) => a[key] !== undefined);
        const other = Object.keys(b).filter((key) => b[key] !== undefined);
        return (keys.length === other.length &&
            keys.every((key) => deepEqual(a[key], b[key])));
    }
    return false;
};
// ---------------------------------------------------------------------------
// Labels and the conditions a field type offers
/** The words on screen for a stored condition (is one of / is not one of read "is any of"). */
const conditionLabel = (condition) => {
    switch (condition) {
        case "on":
            return "is on";
        case "before":
            return "is before";
        case "after":
            return "is after";
        case "is one of":
            return "is any of";
        case "is not one of":
            return "is not any of";
        default:
            return condition;
    }
};
exports.conditionLabel = conditionLabel;
const choice = (condition) => ({
    label: (0, exports.conditionLabel)(condition),
    condition,
});
const TEXT_CONDITIONS = [
    "is",
    "is not",
    "contains",
    "does not contain",
    "starts with",
    "does not start with",
    "ends with",
    "does not end with",
    "is one of",
    "is not one of",
    "is empty",
    "is not empty",
];
const PICKLIST_CONDITIONS = [
    "is",
    "is not",
    "is one of",
    "is not one of",
    "is empty",
    "is not empty",
];
const MULTI_CONDITIONS = [
    "is any of",
    "is not any of",
    "is empty",
    "is not empty",
];
const NUMBER_CONDITIONS = [
    "is",
    "is not",
    "is more than",
    "is less than",
    "is one of",
    "is not one of",
    "is empty",
    "is not empty",
];
const DATE_CONDITIONS = [
    "on",
    "before",
    "after",
    "is empty",
    "is not empty",
];
const NUMBER_TYPES = ["int", "long", "double", "currency", "percent"];
/**
 * The conditions a Salesforce field type offers, in this order (describe types
 * are lower case). "is any of" and "is not any of" store the legacy is one of /
 * is not one of on every type but a multi-select picklist, which stores the new
 * words, so a flat list stays flat. A type this does not know, and no type at
 * all (the screen has no describe), is treated as text.
 */
const conditionsFor = (type) => {
    const lower = typeof type === "string" ? type.toLowerCase() : "";
    let words = TEXT_CONDITIONS;
    if (lower === "picklist")
        words = PICKLIST_CONDITIONS;
    else if (lower === "multipicklist")
        words = MULTI_CONDITIONS;
    else if (NUMBER_TYPES.includes(lower))
        words = NUMBER_CONDITIONS;
    else if (lower === "boolean")
        words = ["is"];
    else if (lower === "date" || lower === "datetime")
        words = DATE_CONDITIONS;
    return words.map(choice);
};
exports.conditionsFor = conditionsFor;
/** The side of an object in a stored value, or undefined when it is not an object. */
const storedSideOf = (stored, object) => {
    const side = isPlainObject(stored) ? stored[object] : undefined;
    return isPlainObject(side) ? side : undefined;
};
exports.storedSideOf = storedSideOf;
/**
 * Whether a side holds the new shape. A `rules` key that is absent or null is
 * no `rules` key. Anything else is one, however broken: it wins over
 * `filterProperties`, and readableOrWhy says what is wrong with it.
 */
const hasRules = (side) => side.rules !== undefined && side.rules !== null;
const rulesOfList = (list) => Array.isArray(list) ? list : [];
/**
 * One side as groups and Keep out, whatever it holds. Never throws: a part it
 * cannot read is left out (readableOrWhy and validateForSave are what refuse).
 *  - nothing, `{}`, an empty or non-array `filterProperties`: none;
 *  - a non-empty `filterProperties`: flat, one group;
 *  - `rules`: rules, and it wins over `filterProperties`.
 * A side the loader refuses (readableOrWhy) carries `refused`, so that
 * sameMatching can tell it from one the poll reads.
 */
const readSideOf = (side) => {
    if (!isPlainObject(side))
        return { source: "none", groups: [], keepOut: [] };
    const checked = (0, exports.readableOrWhy)(side);
    const refused = checked.ok
        ? {}
        : { refused: { path: checked.path, problem: checked.problem } };
    if (hasRules(side)) {
        const { rules } = side;
        const groups = [];
        const include = isPlainObject(rules) ? rules.include : undefined;
        if (isPlainObject(include) && Array.isArray(include.conditions)) {
            include.conditions.forEach((group) => {
                if (isPlainObject(group) && Array.isArray(group.conditions)) {
                    groups.push(group.conditions);
                }
            });
        }
        const exclude = isPlainObject(rules) ? rules.exclude : undefined;
        return Object.assign({ source: "rules", groups, keepOut: isPlainObject(exclude) ? rulesOfList(exclude.conditions) : [] }, refused);
    }
    const list = side.filterProperties;
    if (Array.isArray(list) && list.length > 0) {
        return Object.assign({ source: "flat", groups: [list], keepOut: [] }, refused);
    }
    return Object.assign({ source: "none", groups: [], keepOut: [] }, refused);
};
exports.readSideOf = readSideOf;
/** The side of an object in the account's stored syncCriteria, read (see readSideOf). */
const readSide = (stored, object) => (0, exports.readSideOf)(isPlainObject(stored) ? stored[object] : undefined);
exports.readSide = readSide;
/**
 * Flat: at most one group, no Keep out rule, and only the 14 legacy words.
 * Everything that is flat is saved as `filterProperties`, as today.
 */
const isFlat = (side) => side.groups.length <= 1 &&
    side.keepOut.length === 0 &&
    side.groups.every((group) => group.every((rule) => isLegacy(rule === null || rule === void 0 ? void 0 : rule.condition)));
exports.isFlat = isFlat;
/**
 * The fields the rules use, once each (compared without regard to case, as SOQL
 * compares names), spelled as the first rule that names them spells them:
 * groups first, then Keep out.
 */
const fieldsOf = (side) => {
    const seen = new Set();
    const names = [];
    [...side.groups.flat(), ...side.keepOut].forEach((rule) => {
        const name = rule === null || rule === void 0 ? void 0 : rule.propertyName;
        if (typeof name !== "string" || name === "")
            return;
        const lower = name.toLowerCase();
        if (seen.has(lower))
            return;
        seen.add(lower);
        names.push(name);
    });
    return names;
};
exports.fieldsOf = fieldsOf;
/**
 * A rule as it is stored: a value only where the condition takes one, a list
 * only for the list words. A flat rule comes out as the old tab writes it.
 */
const ruleToStored = (rule) => {
    if ((0, exports.takesNoValue)(rule.condition)) {
        return { propertyName: rule.propertyName, condition: rule.condition };
    }
    if ((0, exports.takesValueList)(rule.condition)) {
        return {
            propertyName: rule.propertyName,
            condition: rule.condition,
            propertyValues: rule.propertyValues,
        };
    }
    return {
        propertyName: rule.propertyName,
        condition: rule.condition,
        propertyValue: rule.propertyValue,
    };
};
exports.ruleToStored = ruleToStored;
/**
 * What the screen writes for one side (2.2 of the criteria design):
 *  - the draft equals what the side loaded as: the side exactly as loaded, byte
 *    for byte, whatever it holds (the screen never re-serialises a side it did
 *    not change);
 *  - flat (0 or 1 group, no Keep out, the 14 words): exactly what the screen
 *    wrote before groups, `{ filterProperties: [...] }`, or `{}` for a side that
 *    never had a list. No `rules` key: an existing one is removed;
 *  - grouped: `rules` is the draft, and `filterProperties` is copied through
 *    unchanged from the side's base (absent stays absent).
 * `storedBefore` is the side's base: what was stored, or on a restore the side
 * of the rules being restored.
 */
const toStored = (draft, storedBefore) => {
    var _a;
    const base = isPlainObject(storedBefore)
        ? storedBefore
        : undefined;
    if (base !== undefined) {
        const loaded = (0, exports.readSideOf)(base);
        if (deepEqual(loaded.groups, draft.groups) &&
            deepEqual(loaded.keepOut, draft.keepOut)) {
            return base;
        }
    }
    if ((0, exports.isFlat)(draft)) {
        const rules = (_a = draft.groups[0]) !== null && _a !== void 0 ? _a : [];
        const listed = base !== undefined && Array.isArray(base.filterProperties);
        if (rules.length === 0 && !listed)
            return {};
        return { filterProperties: rules.map(exports.ruleToStored) };
    }
    const rules = {
        version: 1,
        include: {
            type: "group",
            operator: "or",
            conditions: draft.groups.map((group) => ({
                type: "group",
                operator: "and",
                conditions: group.map(exports.ruleToStored),
            })),
        },
        exclude: {
            type: "group",
            operator: "or",
            conditions: draft.keepOut.map(exports.ruleToStored),
        },
    };
    return base !== undefined && base.filterProperties !== undefined
        ? { filterProperties: base.filterProperties, rules }
        : { rules };
};
exports.toStored = toStored;
/**
 * What goes into the fingerprint of a side (criteriaKeyOf hashes it), and never
 * throws: the raw `filterProperties` for a side without `rules` (so a flat
 * account hashes as it always did), `{ rules }` for a side with one, and
 * undefined for a side that is not an object. Sync Health and Resync build the
 * fingerprint from this, not from the loader, so an unreadable list cannot stop
 * either.
 */
const criteriaKeyInputOf = (storedSide) => {
    if (!isPlainObject(storedSide))
        return undefined;
    if (hasRules(storedSide))
        return { rules: storedSide.rules };
    return storedSide.filterProperties;
};
exports.criteriaKeyInputOf = criteriaKeyInputOf;
/**
 * The objects whose side of a stored or submitted value holds `rules`, in the
 * order Lead, Contact. A `rules` key that is absent or null is no `rules` key
 * (hasRules). The generic save route refuses a value like this on v1 and
 * shadow, whose code reads `filterProperties` only, and the staff switch from
 * v2 to v1 warns about it. Never throws: a value that is not an object, or a
 * side that is not, holds none.
 */
const sidesWithRules = (value) => isPlainObject(value)
    ? exports.SYNC_OBJECTS.filter((object) => isPlainObject(value[object]) &&
        hasRules(value[object]))
    : [];
exports.sidesWithRules = sidesWithRules;
/**
 * A text for a value that does not depend on the order its object keys were
 * written in. Array order stays (it is part of the value).
 */
const canonical = (value) => {
    var _a;
    if (Array.isArray(value))
        return `[${value.map(canonical).join(",")}]`;
    if (isPlainObject(value)) {
        return `{${Object.keys(value)
            .filter((key) => value[key] !== undefined)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`)
            .join(",")}}`;
    }
    return (_a = JSON.stringify(value)) !== null && _a !== void 0 ? _a : "null";
};
/**
 * A rule as the matcher sees it: every field exact, except the value of a
 * condition that takes none (is empty, is not empty), which the matcher ignores
 * (encharge-matchers.es5.js:119-131) and the screen does not send.
 */
const ruleText = (rule) => {
    if (isPlainObject(rule) && (0, exports.takesNoValue)(rule.condition)) {
        const { propertyValue, propertyValues } = rule, rest = __rest(rule, ["propertyValue", "propertyValues"]);
        return canonical(rest);
    }
    return canonical(rule);
};
/** Rules as a set: the order they are written in, and a rule written twice, do not matter. */
const rulesText = (rules) => JSON.stringify(Array.from(new Set(rules.map(ruleText))).sort());
/**
 * Whether two readings of a side (readSide of the side as loaded, and of the
 * draft in its stored shape) choose the same records: the same groups, the same
 * rules in each group and the same Keep out rules, each compared as a set, with
 * every field of every rule exact (7.3 of the criteria design). Rules or groups
 * moved around are the same, and so is a flat list against one group in `rules`
 * holding the same rules. A change that means the same written another way, for
 * example "is any of Webinar, Trade show" turned into "Trade show, Webinar", is
 * not the same: that errs on the safe side. It compares readings, never stored
 * JSON, so a side the screen re-serialised compares the same as the one it loaded.
 *
 * A side the loader refuses (`refused`, see readSideOf) is never the same as one
 * it reads: the poll of an object whose saved side is refused is stopped, so any
 * readable save changes who matches, however alike the groups look (finding 2 of
 * the build check). Two refused sides are compared as readings, as before: a side
 * the screen sent back exactly as it loaded stays refused after the save, and
 * the save changes nothing for that object.
 */
const sameMatching = (a, b) => {
    if (Boolean(a.refused) !== Boolean(b.refused))
        return false;
    const groupsText = (side) => JSON.stringify(Array.from(new Set(side.groups.map(rulesText))).sort());
    return (groupsText(a) === groupsText(b) &&
        rulesText(a.keepOut) === rulesText(b.keepOut));
};
exports.sameMatching = sameMatching;
// ---------------------------------------------------------------------------
// The Opportunity side
/**
 * The window of Opportunity sync in whole months (the same numbers as
 * opportunitySync.ts, which cannot be imported here: it imports this file; a
 * spec compares the two).
 */
exports.OPPORTUNITY_SIDE_WINDOW = {
    min: 1,
    max: 12,
    default: 12,
};
/** The keys an Opportunity side may hold. */
exports.OPPORTUNITY_SIDE_KEYS = ["off", "windowMonths", "rules"];
/** A whole number of months from 1 to 12. */
const isOpportunityWindow = (value) => typeof value === "number" &&
    Number.isInteger(value) &&
    value >= exports.OPPORTUNITY_SIDE_WINDOW.min &&
    value <= exports.OPPORTUNITY_SIDE_WINDOW.max;
exports.isOpportunityWindow = isOpportunityWindow;
/** The stored Opportunity side of a stored `syncCriteria`, or undefined when it is not an object. */
const storedOpportunitySideOf = (stored) => {
    const side = isPlainObject(stored) ? stored[exports.OPPORTUNITY_SIDE] : undefined;
    return isPlainObject(side) ? side : undefined;
};
exports.storedOpportunitySideOf = storedOpportunitySideOf;
/**
 * The Opportunity rules of a stored value as groups and Keep out, whatever it
 * holds. Only the `rules` key is read (an Opportunity side has no flat list, and
 * a stray `filterProperties` is not read). Never throws; a side the loader
 * refuses carries `refused`, as for a people side (readSideOf).
 */
const readOpportunitySide = (stored) => {
    const side = (0, exports.storedOpportunitySideOf)(stored);
    return (0, exports.readSideOf)(side === undefined || side.rules === undefined || side.rules === null
        ? undefined
        : { rules: side.rules });
};
exports.readOpportunitySide = readOpportunitySide;
/** The switch: the customer turned Opportunity sync off (`off: true`, exactly). */
const opportunityOffOf = (stored) => { var _a; return ((_a = (0, exports.storedOpportunitySideOf)(stored)) === null || _a === void 0 ? void 0 : _a.off) === true; };
exports.opportunityOffOf = opportunityOffOf;
/** The window in months: the stored one, else the default 12 (also for a stored value that cannot be read). */
const opportunityWindowOf = (stored) => {
    var _a;
    const months = (_a = (0, exports.storedOpportunitySideOf)(stored)) === null || _a === void 0 ? void 0 : _a.windowMonths;
    return (0, exports.isOpportunityWindow)(months) ? months : exports.OPPORTUNITY_SIDE_WINDOW.default;
};
exports.opportunityWindowOf = opportunityWindowOf;
/**
 * Whether two stored values choose the same Opportunities: the same switch, the
 * same window (a missing one is 12) and the same rules (sameMatching: order and
 * repeats do not count). Compares readings, never stored JSON.
 */
const sameOpportunityMatching = (a, b) => (0, exports.opportunityOffOf)(a) === (0, exports.opportunityOffOf)(b) &&
    (0, exports.opportunityWindowOf)(a) === (0, exports.opportunityWindowOf)(b) &&
    (0, exports.sameMatching)((0, exports.readOpportunitySide)(a), (0, exports.readOpportunitySide)(b));
exports.sameOpportunityMatching = sameOpportunityMatching;
/** A rule as a text that does not depend on the order its keys were written in. */
const ruleKey = (rule) => ruleText(rule);
/**
 * Whether rules A choose no Opportunity that rules B (the stored rules) do not,
 * as far as the rules say it: a CONSERVATIVE test, never "yes" when it is not
 * sure. It is yes when
 *  - every Keep out rule of B is a Keep out rule of A (A keeps out at least as many), and
 *  - B has no include groups (everyone passes that part), or every group of A has
 *    all the rules of some group of B (each way in that A has is a way in that
 *    is at least as strict as one of B's).
 * Anything else (a rule taken away, a group added, a rule changed) is "not sure",
 * so the caller reads again. A flat reading and a grouped one compare alike.
 */
const rulesAreOnlyTighter = (a, b) => {
    const aKeepOut = new Set(a.keepOut.map(ruleKey));
    if (!b.keepOut.every((rule) => aKeepOut.has(ruleKey(rule))))
        return false;
    if (b.groups.length === 0)
        return true;
    if (a.groups.length === 0)
        return false;
    const bGroups = b.groups.map((group) => new Set(group.map(ruleKey)));
    return a.groups.every((group) => {
        const have = new Set(group.map(ruleKey));
        return bGroups.some((needed) => Array.from(needed).every((key) => have.has(key)));
    });
};
exports.rulesAreOnlyTighter = rulesAreOnlyTighter;
const opportunityChangeOf = (stored, next) => {
    const wasOff = (0, exports.opportunityOffOf)(stored);
    const isOff = (0, exports.opportunityOffOf)(next);
    const windowFrom = (0, exports.opportunityWindowOf)(stored);
    const windowTo = (0, exports.opportunityWindowOf)(next);
    const before = (0, exports.readOpportunitySide)(stored);
    const after = (0, exports.readOpportunitySide)(next);
    const same = (0, exports.sameMatching)(before, after);
    const rules = same
        ? "same"
        : (0, exports.rulesAreOnlyTighter)(after, before)
            ? "tighter"
            : "other";
    const switchedOn = wasOff && !isOff;
    const widened = windowTo > windowFrom;
    const fullRead = !isOff && (switchedOn || rules === "other");
    return {
        changed: !(0, exports.sameOpportunityMatching)(stored, next),
        switchedOn,
        switchedOff: !wasOff && isOff,
        windowFrom,
        windowTo,
        widened,
        shortened: windowTo < windowFrom,
        rules,
        readsMore: !isOff && (fullRead || widened),
        fullRead,
    };
};
exports.opportunityChangeOf = opportunityChangeOf;
/**
 * What goes into the fingerprint of the Opportunity side (the API's
 * criteriaKeyOfStored hashes it with Lead and Contact): undefined for a side that
 * is not an object or holds none of its three keys, so an account that never
 * chose anything for Opportunities has the fingerprint it had before this side
 * existed. Otherwise the three keys in a fixed order, `rules` as stored. Never
 * throws.
 */
const opportunityKeyInputOf = (storedSide) => {
    if (!isPlainObject(storedSide))
        return undefined;
    const has = (key) => storedSide[key] !== undefined && storedSide[key] !== null;
    if (!has("off") && !has("windowMonths") && !has("rules"))
        return undefined;
    return Object.assign(Object.assign(Object.assign({}, (has("off") ? { off: storedSide.off } : {})), (has("windowMonths") ? { windowMonths: storedSide.windowMonths } : {})), (has("rules") ? { rules: storedSide.rules } : {}));
};
exports.opportunityKeyInputOf = opportunityKeyInputOf;
/**
 * What the screen writes for the Opportunity side (the counterpart of toStored):
 *  - the draft chooses what `storedBefore` chooses (sameOpportunityMatching): the
 *    side exactly as it loaded, byte for byte;
 *  - else `{ off: true? , windowMonths?, rules? }`: `off` only while it is off, the
 *    window when the stored side had one or it is not 12, `rules` (always the
 *    grouped shape: Opportunity has no flat list) only when there is a rule. A
 *    side with nothing to say is `{}`.
 * `storedBefore` is the whole `syncCriteria` value the draft was read from (or the
 * rules being restored).
 */
const toStoredOpportunity = (draft, storedBefore) => {
    const base = (0, exports.storedOpportunitySideOf)(storedBefore);
    const candidate = Object.assign(Object.assign(Object.assign({}, (draft.off ? { off: true } : {})), ((base === null || base === void 0 ? void 0 : base.windowMonths) !== undefined ||
        draft.windowMonths !== exports.OPPORTUNITY_SIDE_WINDOW.default
        ? { windowMonths: draft.windowMonths }
        : {})), (draft.groups.length > 0 || draft.keepOut.length > 0
        ? {
            rules: {
                version: 1,
                include: {
                    type: "group",
                    operator: "or",
                    conditions: draft.groups.map((group) => ({
                        type: "group",
                        operator: "and",
                        conditions: group.map(exports.ruleToStored),
                    })),
                },
                exclude: {
                    type: "group",
                    operator: "or",
                    conditions: draft.keepOut.map(exports.ruleToStored),
                },
            },
        }
        : {}));
    if (base !== undefined &&
        (0, exports.sameOpportunityMatching)({ [exports.OPPORTUNITY_SIDE]: base }, { [exports.OPPORTUNITY_SIDE]: candidate })) {
        return base;
    }
    return candidate;
};
exports.toStoredOpportunity = toStoredOpportunity;
/**
 * One rule. `strict` is the save check: the limits, a value that must be there,
 * no dots in a field name. The loader (not strict) checks only what makes the
 * answer unknowable.
 */
const problemsOfRule = (rule, path, where) => {
    const { inRules, strict } = where;
    if (!isPlainObject(rule))
        return [{ path, problem: "badShape" }];
    const found = [];
    const add = (field, problem) => found.push({ path: `${path}.${field}`, problem });
    const name = rule.propertyName;
    if (typeof name !== "string" || name === "") {
        add("propertyName", strict ? "field" : "badField");
    }
    else if (!exports.FIELD_NAME.test(name) ||
        (strict && (name.includes(".") || name.length > exports.LIMITS.fieldName))) {
        add("propertyName", "badField");
    }
    const { condition } = rule;
    if (!isLegacy(condition) && !(inRules && isNew(condition))) {
        add("condition", "unknownCondition");
        return found;
    }
    if ((0, exports.takesNoValue)(condition))
        return found;
    if ((0, exports.takesValueList)(condition)) {
        const list = rule.propertyValues;
        if (!Array.isArray(list) || list.length === 0) {
            add("propertyValues", "chooseMany");
        }
        else if (strict) {
            if (list.length > exports.LIMITS.listValues) {
                add("propertyValues", "tooMany");
            }
            else if (list.some((item) => typeof item !== "string" || item === "")) {
                add("propertyValues", "value");
            }
            else if (list.some((item) => item.length > exports.LIMITS.listItem)) {
                add("propertyValues", "tooLong");
            }
        }
        else if (list.some((item) => typeof item !== "string")) {
            add("propertyValues", "chooseMany");
        }
        return found;
    }
    const text = rule.propertyValue;
    if ((0, exports.takesDate)(condition)) {
        if (!(0, exports.isRealDate)(text))
            add("propertyValue", "date");
        return found;
    }
    if (strict) {
        if (typeof text !== "string" || text === "") {
            add("propertyValue", "value");
            return found;
        }
        if (text.length > exports.LIMITS.value) {
            add("propertyValue", "tooLong");
            return found;
        }
        if ((0, exports.takesCommaList)(condition) &&
            text.split(",").length > exports.LIMITS.listValues) {
            add("propertyValue", "tooMany");
            return found;
        }
    }
    // inside `rules` only: a flat list keeps today's rule (the matcher passes a
    // value that is not a number), so lists v1 can read are not refused
    if (inRules && takesNumber(condition) && !(0, exports.isFiniteNumberText)(text)) {
        add("propertyValue", "number");
    }
    return found;
};
/** A list of rules (a flat list, a group or Keep out) with its limit. */
const problemsOfList = (list, path, where) => {
    if (!Array.isArray(list))
        return [{ path, problem: "badShape" }];
    const found = [];
    if (where.empty && list.length === 0) {
        found.push({ path, problem: "emptyGroup" });
    }
    if (where.strict) {
        const max = where.limit === "overKeepOut" ? exports.LIMITS.keepOut : exports.LIMITS.rulesPerGroup;
        if (list.length > max)
            found.push({ path, problem: where.limit });
    }
    list.forEach((rule, index) => {
        found.push(...problemsOfRule(rule, `${path}[${index}]`, where));
    });
    return found;
};
/** `rules`: the version, the wrappers, every group and Keep out, and on a save the limits. */
const problemsOfRules = (rules, strict) => {
    const path = "rules";
    if (!isPlainObject(rules))
        return [{ path, problem: "badShape" }];
    const found = [];
    if (rules.version !== 1)
        found.push({ path: `${path}.version`, problem: "badShape" });
    let total = 0;
    const { include } = rules;
    if (include !== undefined) {
        const at = `${path}.include`;
        if (!isPlainObject(include) ||
            include.type !== "group" ||
            include.operator !== "or" ||
            !Array.isArray(include.conditions)) {
            found.push({ path: at, problem: "badShape" });
        }
        else {
            if (strict && include.conditions.length > exports.LIMITS.groups) {
                found.push({ path: `${at}.conditions`, problem: "overGroups" });
            }
            include.conditions.forEach((group, index) => {
                const groupAt = `${at}.conditions[${index}]`;
                if (!isPlainObject(group) ||
                    group.type !== "group" ||
                    group.operator !== "and") {
                    found.push({ path: groupAt, problem: "badShape" });
                    return;
                }
                if (Array.isArray(group.conditions))
                    total += group.conditions.length;
                found.push(...problemsOfList(group.conditions, `${groupAt}.conditions`, {
                    inRules: true,
                    strict,
                    limit: "overRules",
                    empty: true,
                }));
            });
        }
    }
    const { exclude } = rules;
    if (exclude !== undefined) {
        const at = `${path}.exclude`;
        if (!isPlainObject(exclude) ||
            exclude.type !== "group" ||
            exclude.operator !== "or") {
            found.push({ path: at, problem: "badShape" });
        }
        else {
            if (Array.isArray(exclude.conditions))
                total += exclude.conditions.length;
            found.push(...problemsOfList(exclude.conditions, `${at}.conditions`, {
                inRules: true,
                strict,
                limit: "overKeepOut",
                empty: false,
            }));
        }
    }
    if (strict && total > exports.LIMITS.rulesPerObject) {
        found.push({ path, problem: "overTotal" });
    }
    return found;
};
/**
 * The loader's check of one stored side: ok, or the first thing that makes the
 * answer unknowable. It refuses only that, so that what syncs today keeps
 * syncing (never a size limit, never a value on is empty / is not empty):
 *  - flat side: an entry that is not a plain object, a field name that is not a
 *    string or fails FIELD_NAME, a condition outside the 14 legacy words;
 *  - side with `rules`: `rules` not an object, a version other than 1, a wrong
 *    operator or wrapper, a group that is not a list of rules, an empty group,
 *    and per rule the flat checks plus a date word without a real day, is any
 *    of / is not any of without a non-empty list of text, is more than / is less
 *    than without a finite number. `filterProperties` beside `rules` is not read.
 *  - kept as today, not refused: a side that is absent, null or not an object,
 *    and a `filterProperties` that is absent, empty or not a list: no rules.
 */
const readableOrWhy = (storedSide) => {
    if (!isPlainObject(storedSide))
        return { ok: true };
    const found = hasRules(storedSide)
        ? problemsOfRules(storedSide.rules, false)
        : Array.isArray(storedSide.filterProperties)
            ? storedSide.filterProperties.flatMap((rule, index) => problemsOfRule(rule, `filterProperties[${index}]`, {
                inRules: false,
                strict: false,
            }))
            : [];
    return found.length === 0 ? { ok: true } : Object.assign({ ok: false }, found[0]);
};
exports.readableOrWhy = readableOrWhy;
/**
 * The save check: every problem of the sides this save changes, limits
 * included. A side that is deep-equal to the stored one is not checked at all
 * (an unchanged list over a limit, or one the loader refuses, never blocks a save
 * of the other object), and the `filterProperties` copied through beside `rules`
 * gets no check (the loader ignores it while `rules` exists).
 * `stored` is the account's current syncCriteria.
 */
const validateForSave = (value, { stored } = {}) => {
    const found = [];
    if (!isPlainObject(value)) {
        return [{ object: null, path: "", problem: "badShape" }];
    }
    Object.keys(value).forEach((key) => {
        if (key !== "Lead" &&
            key !== "Contact" &&
            key !== exports.OPPORTUNITY_SIDE &&
            key !== exports.CAMPAIGN_SIDE) {
            found.push({ object: null, path: key, problem: "badShape" });
        }
    });
    const sideChanged = (object) => !deepEqual(value[object], isPlainObject(stored) ? stored[object] : undefined);
    const changed = exports.SYNC_OBJECTS.some(sideChanged) ||
        sideChanged(exports.OPPORTUNITY_SIDE) ||
        sideChanged(exports.CAMPAIGN_SIDE);
    if (changed) {
        let bytes = 0;
        try {
            bytes = JSON.stringify(value).length;
        }
        catch (e) {
            return [...found, { object: null, path: "", problem: "badShape" }];
        }
        if (bytes > exports.LIMITS.bytes) {
            found.push({ object: null, path: "", problem: "tooBig" });
        }
    }
    exports.SYNC_OBJECTS.forEach((object) => {
        const side = value[object];
        const before = isPlainObject(stored) ? stored[object] : undefined;
        if (deepEqual(side, before))
            return;
        const add = (problems) => problems.forEach((p) => found.push(Object.assign({ object }, p)));
        if (side === undefined)
            return;
        if (!isPlainObject(side)) {
            add([{ path: "", problem: "badShape" }]);
            return;
        }
        if (hasRules(side)) {
            add(problemsOfRules(side.rules, true));
            return;
        }
        const list = side.filterProperties;
        if (list === undefined)
            return;
        add(problemsOfList(list, "filterProperties", {
            inRules: false,
            strict: true,
            limit: "overRules",
            empty: false,
        }));
        if (Array.isArray(list) && list.length > exports.LIMITS.rulesPerObject) {
            add([{ path: "filterProperties", problem: "overTotal" }]);
        }
    });
    if (sideChanged(exports.OPPORTUNITY_SIDE) && value[exports.OPPORTUNITY_SIDE] !== undefined) {
        problemsOfOpportunitySide(value[exports.OPPORTUNITY_SIDE]).forEach((problem) => found.push(Object.assign({ object: exports.OPPORTUNITY_SIDE }, problem)));
    }
    if (sideChanged(exports.CAMPAIGN_SIDE) && value[exports.CAMPAIGN_SIDE] !== undefined) {
        problemsOfCampaignSide(value[exports.CAMPAIGN_SIDE]).forEach((problem) => found.push(Object.assign({ object: exports.CAMPAIGN_SIDE }, problem)));
    }
    return found;
};
exports.validateForSave = validateForSave;
/**
 * The save check of the Opportunity side: a plain object with only `off` (true or
 * false), `windowMonths` (a whole number from 1 to 12) and `rules` (the grouped
 * shape, with the limits of a save). A flat `filterProperties` is not a shape an
 * Opportunity side has, so it is refused as any other key is.
 */
const problemsOfOpportunitySide = (side) => {
    if (!isPlainObject(side))
        return [{ path: "", problem: "badShape" }];
    const found = [];
    Object.keys(side).forEach((key) => {
        if (!exports.OPPORTUNITY_SIDE_KEYS.includes(key)) {
            found.push({ path: key, problem: "badShape" });
        }
    });
    if (side.off !== undefined && typeof side.off !== "boolean") {
        found.push({ path: "off", problem: "badShape" });
    }
    if (side.windowMonths !== undefined &&
        !(0, exports.isOpportunityWindow)(side.windowMonths)) {
        found.push({ path: "windowMonths", problem: "window" });
    }
    if (side.rules !== undefined && side.rules !== null) {
        found.push(...problemsOfRules(side.rules, true));
    }
    return found;
};
/**
 * The keys a Campaign side may hold, and the numbers its save check holds them
 * to. campaignSync.ts (which imports this file) has the same figures under its
 * own names; a spec compares the two.
 */
exports.CAMPAIGN_SIDE_KEYS = [
    "off",
    "windowMonths",
    "includeActive",
    "includeRecent",
    "picks",
    "rules",
];
exports.CAMPAIGN_SIDE_WINDOW = { min: 1, max: 12, default: 12 };
/** The most Campaigns that can be picked by hand: the length of the picker's list. */
exports.CAMPAIGN_SIDE_MAX_PICKS = 2000;
const CAMPAIGN_ID_18 = /^[A-Za-z0-9]{18}$/;
/**
 * The save check of the Campaign side: a plain object with only `off`,
 * `includeActive` and `includeRecent` (true or false), `windowMonths` (a whole
 * number from 1 to 12), `picks` (a list of 18 character Campaign ids, each once,
 * at most CAMPAIGN_SIDE_MAX_PICKS) and `rules` (the grouped shape, with the limits
 * of a save). A flat `filterProperties` is not a shape a Campaign side has, so it
 * is refused as any other key is.
 */
const problemsOfCampaignSide = (side) => {
    if (!isPlainObject(side))
        return [{ path: "", problem: "badShape" }];
    const found = [];
    Object.keys(side).forEach((key) => {
        if (!exports.CAMPAIGN_SIDE_KEYS.includes(key)) {
            found.push({ path: key, problem: "badShape" });
        }
    });
    ["off", "includeActive", "includeRecent"].forEach((key) => {
        if (side[key] !== undefined && typeof side[key] !== "boolean") {
            found.push({ path: key, problem: "badShape" });
        }
    });
    if (side.windowMonths !== undefined &&
        !(typeof side.windowMonths === "number" &&
            Number.isInteger(side.windowMonths) &&
            side.windowMonths >= exports.CAMPAIGN_SIDE_WINDOW.min &&
            side.windowMonths <= exports.CAMPAIGN_SIDE_WINDOW.max)) {
        found.push({ path: "windowMonths", problem: "window" });
    }
    if (side.picks !== undefined) {
        if (!Array.isArray(side.picks)) {
            found.push({ path: "picks", problem: "badShape" });
        }
        else {
            const seen = new Set();
            side.picks.forEach((pick, index) => {
                if (typeof pick !== "string" ||
                    !CAMPAIGN_ID_18.test(pick) ||
                    seen.has(pick)) {
                    found.push({ path: `picks[${index}]`, problem: "picks" });
                    return;
                }
                seen.add(pick);
            });
            if (side.picks.length > exports.CAMPAIGN_SIDE_MAX_PICKS) {
                found.push({ path: "picks", problem: "picks" });
            }
        }
    }
    if (side.rules !== undefined && side.rules !== null) {
        found.push(...problemsOfRules(side.rules, true));
    }
    return found;
};
//# sourceMappingURL=salesforceSyncCriteria.js.map